import React from 'react';
import { Image } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const HeroEditor = ({ content, onContentChange, onMultipleContentChanges, uploading, onImageUpload, onOpenImagePicker, site, onCreateCustomTheme }) => {
  return (
    <div className="space-y-2">
      {/* Section Heading */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section heading</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => onContentChange('headline', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Cozy Beachside Café in Ankara"
        />
      </div>
      
      {/* Paragraph with Rich Text Controls */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-900">Paragraph</label>
          <span className="text-xs text-gray-500">
            {(content.subheading || '').length} / 512
          </span>
        </div>
        <textarea
          rows={3}
          value={content.subheading || ''}
          onChange={(e) => onContentChange('subheading', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="Experience the perfect blend of fresh coffee and ocean breeze at our charming café. We serve locally sourced ingredients in a warm, welcoming atmosphere."
          maxLength={512}
        />
      </div>

      {/* Call to Action */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Call to action</label>
        <input
          type="text"
          value={content.ctaText || ''}
          onChange={(e) => onContentChange('ctaText', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Visit Us Today"
        />
      </div>

      {/* Text Alignment */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Text alignment</label>
        <div className="flex gap-1">
          {[
            { name: 'left', icon: '⬅' },
            { name: 'center', icon: '🎯' },
            { name: 'right', icon: '➡' }
          ].map((align) => (
            <button
              key={align.name}
              onClick={() => onContentChange('alignment', align.name)}
              className={`px-2 py-1 text-xs border rounded ${
                (content.alignment || 'center') === align.name ? 'bg-gray-200 border-gray-400' : 'border-gray-300'
              }`}
            >
              {align.name}
            </button>
          ))}
        </div>
      </div>

      {/* Hero Height */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Hero height</label>
        <div className="flex gap-1">
          {[
            { name: 'small', label: 'Small (30%)' },
            { name: 'medium', label: 'Medium (50%)' },
            { name: 'large', label: 'Large (70%)' },
            { name: 'full', label: 'Full (100%)' }
          ].map((height) => (
            <button
              key={height.name}
              onClick={() => onContentChange('heroHeight', height.name)}
              className={`px-2 py-1 text-xs border rounded ${
                (content.heroHeight || 'full') === height.name ? 'bg-gray-200 border-gray-400' : 'border-gray-300'
              }`}
            >
              {height.label}
            </button>
          ))}
        </div>
      </div>

      {/* Text Shadow */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-900">Text shadow</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="textShadowToggle"
              type="checkbox" 
              checked={content.textShadow?.enabled || false}
              onChange={(e) => {
                if (e.target.checked) {
                  onContentChange('textShadow', {
                    enabled: true,
                    style: 'subtle',
                    color: 'rgba(0, 0, 0, 0.3)'
                  });
                } else {
                  onContentChange('textShadow', {
                    enabled: false,
                    style: 'subtle',
                    color: 'rgba(0, 0, 0, 0.3)'
                  });
                }
              }}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="textShadowToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
        {content.textShadow?.enabled && (
          <div className="space-y-3">
            {/* Shadow Style Presets */}
            <div>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { name: 'subtle', label: 'Subtle' },
                  { name: 'soft', label: 'Soft' },
                  { name: 'medium', label: 'Medium' },
                  { name: 'strong', label: 'Strong' },
                  { name: 'dramatic', label: 'Dramatic' },
                  { name: 'custom', label: 'Custom' }
                ].map((style) => (
                  <button
                    key={style.name}
                    onClick={() => {
                      const updates = {
                        textShadow: {
                          enabled: true,
                          style: style.name,
                          color: content.textShadow?.color || 'rgba(0, 0, 0, 0.3)'
                        }
                      };
                      onContentChange('textShadow', updates.textShadow);
                    }}
                    className={`px-2 py-1 text-xs border rounded ${
                      (content.textShadow?.style || 'subtle') === style.name ? 'bg-gray-200 border-gray-400' : 'border-gray-300'
                    }`}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Custom Color (only when custom style is selected) */}
            {content.textShadow?.style === 'custom' && (
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Custom shadow color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={content.textShadow?.color?.includes('#') ? content.textShadow.color : '#000000'}
                    onChange={(e) => {
                      const updates = {
                        ...content.textShadow,
                        color: e.target.value
                      };
                      onContentChange('textShadow', updates);
                    }}
                    className="w-8 h-6 border border-gray-300 rounded cursor-pointer"
                  />
                  <span className="text-xs text-gray-500">
                    {content.textShadow?.color || 'rgba(0, 0, 0, 0.3)'}
                  </span>
                </div>
              </div>
            )}
            
            {/* Shadow Opacity */}
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1">Shadow opacity</label>
              <div className="flex items-center gap-1">
                <span className="text-xs">○</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={content.textShadow?.opacity || 50}
                  onChange={(e) => {
                    const opacity = parseInt(e.target.value);
                    const updates = {
                      ...content.textShadow,
                      opacity: opacity,
                      color: content.textShadow?.style === 'custom' ? (content.textShadow?.color?.includes('#') ? content.textShadow.color : `rgba(0, 0, 0, ${opacity / 100})`) : `rgba(0, 0, 0, ${opacity / 100})`
                    };
                    onContentChange('textShadow', updates);
                  }}
                  className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs">●</span>
                <span className="text-xs text-gray-500 ml-2">{content.textShadow?.opacity || 50}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Foreground Image */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-900">Foreground image</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="foregroundImageToggle"
              type="checkbox" 
              checked={content.foregroundImage || false}
              onChange={(e) => {
                console.log('Foreground image toggle:', e.target.checked);
                onContentChange('foregroundImage', e.target.checked);
              }}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="foregroundImageToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
        {content.foregroundImage && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div 
                className="flex-1 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                onClick={() => document.getElementById('foregroundImageInput').click()}
              >
                {content.foregroundImageUrl ? (
                  <img 
                    src={content.foregroundImageUrl} 
                    alt="Foreground" 
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <div className="text-center">
                    <Image className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <div className="text-xs text-gray-500">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </div>
                  </div>
                )}
                <input
                  id="foregroundImageInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => onImageUpload(e.target.files[0], 'foregroundImageUrl')}
                  className="hidden"
                  disabled={uploading}
                />
              </div>
              <button
                onClick={() => onOpenImagePicker && onOpenImagePicker('foregroundImageUrl')}
                className="px-3 py-2 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition min-w-0 flex flex-col items-center justify-center"
                disabled={uploading}
              >
                <Image className="w-3 h-3 mb-1" />
                <span>Unsplash</span>
              </button>
            </div>
            
            {/* Photo Credit for Foreground Image */}
            {content.foregroundImageUrlCredit && (
              <div className="text-xs text-gray-500">
                Photo by{' '}
                <a 
                  href={content.foregroundImageUrlCredit.photographerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {content.foregroundImageUrlCredit.photographer}
                </a>
                {' '}on Unsplash
              </div>
            )}
            
            {/* Style and Placement in same row */}
            <div className="grid grid-cols-2 gap-2">
              {/* Style Options */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Style</label>
                <div className="flex gap-1">
                  <button 
                    onClick={() => onContentChange('foregroundStyle', 'fill')}
                    className={`px-2 py-1 text-xs border rounded ${content.foregroundStyle === 'fill' || !content.foregroundStyle ? 'bg-gray-200 border-gray-400' : 'border-gray-300'}`}
                  >
                    Fill
                  </button>
                  <button 
                    onClick={() => onContentChange('foregroundStyle', 'fit')}
                    className={`px-2 py-1 text-xs border rounded ${content.foregroundStyle === 'fit' ? 'bg-gray-200 border-gray-400' : 'border-gray-300'}`}
                  >
                    Fit
                  </button>
                </div>
              </div>

              {/* Placement */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Placement</label>
                <div className="flex gap-1">
                  <button
                    onClick={() => onContentChange('foregroundPlacement', 'left')}
                    className={`px-2 py-1 text-xs border rounded ${
                      content.foregroundPlacement === 'left' || !content.foregroundPlacement 
                        ? 'bg-gray-200 border-gray-400' 
                        : 'border-gray-300'
                    }`}
                    title="Image on left, text on right"
                  >
                    ◀ ◯
                  </button>
                  <button
                    onClick={() => onContentChange('foregroundPlacement', 'right')}
                    className={`px-2 py-1 text-xs border rounded ${
                      content.foregroundPlacement === 'right' 
                        ? 'bg-gray-200 border-gray-400' 
                        : 'border-gray-300'
                    }`}
                    title="Text on left, image on right"
                  >
                    ◯ ▶
                  </button>
                </div>
              </div>
            </div>

            {/* Frame Options */}
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1">Frame</label>
              <div className="flex gap-1">
                {[
                  { name: 'wide', icon: '▬' },
                  { name: 'landscape', icon: '▭' },
                  { name: 'portrait', icon: '▯' },
                  { name: 'square', icon: '■' },
                  { name: 'circle', icon: '●' }
                ].map((frame) => (
                  <button
                    key={frame.name}
                    type="button"
                    onClick={() => {
                      // Update both frame and shape together to avoid stale state
                      const updates = {
                        foregroundFrame: frame.name,
                        foregroundShape: frame.name === 'circle' ? null : 'none'
                      };
                      onMultipleContentChanges(updates);
                    }}
                    className={`w-6 h-6 flex items-center justify-center border rounded transition-all ${
                      content.foregroundFrame === frame.name || (!content.foregroundFrame && frame.name === 'wide') 
                        ? 'bg-gray-200 border-gray-400' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    title={frame.name}
                  >
                    {frame.icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Shape Options */}
            {(content.foregroundFrame !== 'circle') && (
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Shape</label>
                <div className="grid grid-cols-3 gap-1">
                  {(() => {
                    const frame = content.foregroundFrame || 'wide';
                    
                    // Define which shapes are compatible with which frames based on available SVG masks
                    const frameShapeCompatibility = {
                      'wide': [
                        { name: 'none', label: 'None', icon: '⬜' },
                        { name: 'angled', label: 'Angled', icon: '▱' },
                        { name: 'pill', label: 'Pill', icon: '⭕' }
                      ],
                      'landscape': [
                        { name: 'none', label: 'None', icon: '⬜' },
                        { name: 'notched', label: 'Notched', icon: '◎' },
                        { name: 'pill', label: 'Pill', icon: '⭕' }
                      ],
                      'portrait': [
                        { name: 'none', label: 'None', icon: '⬜' },
                        { name: 'arch', label: 'Arch', icon: '⌒' },
                        { name: 'pill', label: 'Pill', icon: '⭕' }
                      ],
                      'square': [
                        { name: 'none', label: 'None', icon: '⬜' },
                        { name: 'angled', label: 'Angled', icon: '▱' },
                        { name: 'bumpy', label: 'Bumpy', icon: '◐' },
                        { name: 'flared', label: 'Flared', icon: '◈' },
                        { name: 'flower', label: 'Flower', icon: '🌸' },
                        { name: 'screen', label: 'Screen', icon: '🖥' }
                      ]
                    };
                    
                    const compatibleShapes = frameShapeCompatibility[frame] || frameShapeCompatibility['wide'];
                    
                    return compatibleShapes.map((shape) => (
                      <button
                        key={shape.name}
                        onClick={() => onContentChange('foregroundShape', shape.name)}
                        className={`px-1 py-1 text-xs border rounded transition-all text-center ${
                          content.foregroundShape === shape.name || (!content.foregroundShape && shape.name === 'none') 
                            ? 'bg-gray-200 border-gray-400' 
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                        title={shape.label}
                      >
                        <div className="text-base leading-none">{shape.icon}</div>
                        <div className="text-xs mt-0.5">{shape.label}</div>
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Size */}
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1">Size</label>
              <div className="flex gap-1">
                {[
                  { name: 'sm', label: 'Small' },
                  { name: 'md', label: 'Medium' },
                  { name: 'lg', label: 'Large' }
                ].map((size) => (
                  <button
                    key={size.name}
                    onClick={() => onContentChange('foregroundSize', size.name)}
                    className={`px-2 py-1 text-xs border rounded ${
                      content.foregroundSize === size.name || (!content.foregroundSize && size.name === 'md') 
                        ? 'bg-gray-200 border-gray-400' 
                        : 'border-gray-300'
                    }`}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Background Image */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-900">Background image</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              type="checkbox" 
              checked={content.backgroundImage || false}
              onChange={(e) => onContentChange('backgroundImage', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
        {content.backgroundImage && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div 
                className="flex-1 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                onClick={() => document.getElementById('backgroundImageInput').click()}
              >
                {content.backgroundImageUrl ? (
                  <img 
                    src={content.backgroundImageUrl} 
                    alt="Background" 
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <div className="text-center">
                    <Image className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <div className="text-xs text-gray-500">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </div>
                  </div>
                )}
                <input
                  id="backgroundImageInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => onImageUpload(e.target.files[0], 'backgroundImageUrl')}
                  className="hidden"
                  disabled={uploading}
                />
              </div>
              <button
                onClick={() => onOpenImagePicker('backgroundImageUrl')}
                className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                disabled={uploading}
              >
                <Image className="w-3 h-3 mb-1" />
                <span>Unsplash</span>
              </button>
            </div>
            {content.backgroundImageUrlCredit && (
              <div className="text-xs text-gray-500">
                Photo by{' '}
                <a 
                  href={content.backgroundImageUrlCredit.photographerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {content.backgroundImageUrlCredit.photographer}
                </a>
                {' '}on Unsplash
              </div>
            )}
            
            {/* Style Options */}
            <div className="flex gap-1">
              <button 
                onClick={() => onContentChange('backgroundStyle', 'fill')}
                className={`px-2 py-1 text-xs border rounded ${content.backgroundStyle === 'fill' || !content.backgroundStyle ? 'bg-gray-200 border-gray-400' : 'border-gray-300'}`}
              >
                Fill
              </button>
              <button 
                onClick={() => onContentChange('backgroundStyle', 'fit')}
                className={`px-2 py-1 text-xs border rounded ${content.backgroundStyle === 'fit' ? 'bg-gray-200 border-gray-400' : 'border-gray-300'}`}
              >
                Fit
              </button>
            </div>

            {/* Overlay Opacity */}
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1">Overlay opacity</label>
              <div className="flex items-center gap-1">
                <span className="text-xs">○</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={content.overlayOpacity || 50}
                  onChange={(e) => onContentChange('overlayOpacity', parseInt(e.target.value))}
                  className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs">●</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorTheme || 'default'}
        onChange={(value) => onContentChange('colorTheme', value === 'default' ? null : value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default HeroEditor;